import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import type { Signal } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	PLATFORM_ID,
	Renderer2,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MarkdownModule } from 'ngx-markdown';
import type { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';

import { AccordionComponent } from '@valk-nx/components/ui-accordion/src/lib/accordion';
import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import type { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import type { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { Sizes } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { PricingComponent } from '@valk-nx/components/ui-pricing/src/lib/pricing';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { ReadMoreComponent } from '@valk-nx/components/ui-read-more/src/lib/read-more';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import {
	HOTEL_SLUG,
	negativeHeader$,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import { AvailabilityWidgetComponent } from '@valk-nx/storyblok/components/availability-widget/src/lib/availability-widget';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import type { PackageData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-package-detail',
	templateUrl: './package-detail.component.html',
	imports: [
		AccordionComponent,
		AvailabilityWidgetComponent,
		CarouselComponent,
		CommonModule,
		ListGridComponent,
		MarkdownModule,
		MapComponent,
		MiniCarouselComponent,
		PricingComponent,
		RatingComponent,
		ReadMoreComponent,
		RouterModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
	],
})
export class PackageDetailComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);
	private readonly metadataService = inject(MetadataService);
	private readonly location = inject(Location);
	private readonly renderer2 = inject(Renderer2);
	private readonly translate = inject(TranslateService);
	readonly platformId = inject(PLATFORM_ID);
	readonly hotelSlug = inject(HOTEL_SLUG);
	readonly bookingtool = inject(BOOKINGTOOL);

	readonly hotelStore = inject(HotelStore);

	isSmallTablet$: Observable<boolean>;
	package$: Observable<PackageData>;
	package: Signal<PackageData> = signal<PackageData>(null);

	sizes = Sizes;
	images: CarouselItem[] = [];
	hotelImages: ImageInterface[] = [];
	activePackageIndex = signal<number>(null);
	activePackage = computed(() => {
		return this.package()?.packages?.[this.activePackageIndex()];
	});
	parentUrl = '';
	disabledReadmore = true;
	maxDate: Date | undefined;
	minDate: Date;

	intialRunActivePackageIndex = true;
	language: string;

	pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	constructor() {
		super();
		negativeHeader$.next(true);

		this.language =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();
		this.package$ = this.sbService.translatedSlugsForCurrentStory().pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
			switchMap((slug) => {
				this.parentUrl = slug.path.substring(
					0,
					slug.path.lastIndexOf('/') + 1,
				);

				return this.contentService.getPackage(
					slug.path.split('/').pop(),
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				const packageData = response.data;
				let hash = '';

				if (isPlatformBrowser(this.platformId)) {
					hash = window.location.hash.substring(1);
				}

				if (hash && packageData.packages) {
					const packageIndex = packageData.packages.findIndex(
						(pkg) => this.formatPackageName(pkg.name) === hash,
					);
					if (packageIndex !== -1) {
						this.activePackageIndex.set(packageIndex);
					} /* v8 ignore start */ else {
						this.activePackageIndex.set(0);
					}
					/* v8 ignore stop */
				} else {
					this.activePackageIndex.set(0); // Default to the first package
				}

				this.metadataService.updateMetaDataBatch(
					packageData,
					this.robotsNoFollow,
					this.robotsNoIndex,
				);

				this.metadataService.setSchemaOrgPackageDetail(
					this.renderer2,
					packageData,
					this.hotelStore.hotelMainContact(),
				);

				this.images = packageData.images.map(
					(image: { src: string; alt: string }) => {
						return {
							image: {
								src: image.src,
								alt: image.alt,
							},
							heading: null,
							subHeading: null,
							link: null,
						};
					},
				);
				this.hotelImages = packageData.hotel?.images.map(
					(image: { src: string; alt: string }) => {
						return {
							src: image.src,
							altText: image.alt,
							width: 0,
							height: 0,
						};
					},
				);

				this.maxDate =
					packageData.bookable.to &&
					dayjs(packageData.bookable.to).isAfter(dayjs(), 'day')
						? dayjs(packageData.bookable.to).toDate()
						: undefined;
				this.minDate =
					packageData.bookable.from &&
					dayjs(packageData.bookable.from).isAfter(dayjs(), 'day')
						? dayjs(packageData.bookable.from).toDate()
						: dayjs().subtract(1, 'day').toDate();
				if (this.images.length === 0) {
					negativeHeader$.next(false);
				} else {
					/* v8 ignore next */
					negativeHeader$.next(true);
				}

				return packageData;
			}),
		);

		effect(() => {
			const activePackageIndex = this.activePackageIndex();

			if (!this.intialRunActivePackageIndex) {
				this.setActivePackageUrl(activePackageIndex);
			}

			this.intialRunActivePackageIndex = false;
		});

		this.package = toSignal(this.package$);
	}

	onPackageSelected(index: number) {
		this.activePackageIndex.set(index);
	}

	setActivePackageUrl(tabIndex: number) {
		if (isPlatformBrowser(this.platformId)) {
			const packageName = this.package()?.packages?.[tabIndex]?.name;
			if (!packageName) {
				return;
			}

			const formattedName = this.formatPackageName(packageName);
			const currentHash = window.location.hash.substring(1);

			// Only update if the hash is actually different
			if (currentHash === formattedName) {
				return;
			}

			const path = this.location.path().split('?')[0];
			this.location.replaceState(path + `#${formattedName}`);
		}
	}

	private formatPackageName(name: string): string {
		return name.trim().replaceAll(/\s+/g, '-');
	}
}
